import React, { useEffect } from 'react';
import { useHistory, useParams } from 'react-router';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { LeadDetailsSkeleton } from 'src/components/Lead/Details/components';
import useAuth from 'src/hooks/useAuth';
import useLead from 'src/hooks/useLead';
import { canAccessLeadDetail } from 'src/modules/lead/guards/canAccessLeadDetail';
import { useLeadQuery } from 'src/modules/lead/queries/useLeadQuery';
import { normalizeError } from 'src/contexts/utils/handleError';

export const LeadAccessGuard = ({ children }) => {
    const { user } = useAuth();
    const { lead, setLead } = useLead();
    const { id } = useParams<{ id: string }>();
    const history = useHistory();
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();
    const { data, error, isError, isFetching, isPending } = useLeadQuery(
        id,
        Boolean(user?._id)
    );

    useEffect(() => {
        if (data && !isFetching) setLead(data);
    }, [data, isFetching, setLead]);

    useEffect(() => {
        if (!lead?._id || lead._id !== id || !user?._id || isFetching) return;

        const hasAccess = canAccessLeadDetail({ user, lead });
        if (!hasAccess) history.replace('/');
    }, [lead, id, user, history, isFetching]);

    useEffect(() => {
        if (!isError) return;

        enqueueSnackbar(t(normalizeError(error)), { variant: 'error' });
    }, [isError, error, enqueueSnackbar, t]);

    if (isError) return null;

    if (isPending || isFetching || !lead || lead._id !== id)
        return <LeadDetailsSkeleton />;

    if (
        !data ||
        !canAccessLeadDetail({
            user,
            lead,
        })
    )
        return null;

    return <>{children}</>;
};
